import React, {
	useCallback,
	type SyntheticEvent,
	useRef,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import { styled } from '@compiled/react';
import { Manager, Popper, Reference } from '@atlaskit/popper';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import type { Feature } from '@atlassian/jira-servicedesk-common/src/service-project-features/types.tsx';

type Props = {
	feature: Feature;
	value?: boolean;
	saving?: boolean;
	disabled?: boolean;
	focusToggle?: boolean;
	popperText?: ReactNode | null;
	labelId?: string;
	isPopperOpenByDefault?: boolean;
};

// It is being referred from jira/src/packages/servicedesk/itsm-features/src/common/ui/feature-toggle-tile/feature-toggle-v2
// Main difference is this handles cases when the pop-up is shadowed by other nearby toggles
export const FeatureToggle = ({
	value = false,
	saving = false,
	disabled = false,
	focusToggle = false,
	popperText = null,
	labelId,
	isPopperOpenByDefault = false,
}: Props) => {
	const [isPopperOpen, setIsPopperOpen] = useState(
		ff('enable_jsm_repackaging_changes_vtwk9') ? isPopperOpenByDefault : false,
	);

	const handleChange = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			// We're not disabling the toggle since we need to use the onchange event to show the popper
			if (disabled) {
				event.preventDefault();
				setIsPopperOpen(true);
			}
		},
		[disabled],
	);

	const toggleRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (focusToggle) {
			toggleRef?.current?.focus();
		}
	}, [focusToggle, toggleRef]);

	useEffect(() => {
		if (ff('enable_jsm_repackaging_changes_vtwk9') && isPopperOpenByDefault) {
			setIsPopperOpen(isPopperOpenByDefault);
		}
	}, [isPopperOpenByDefault]);

	return (
		<Wrapper>
			{saving && (
				<FloatLeftWrapper role="status" aria-label="Loading">
					<Spinner size="small" />
				</FloatLeftWrapper>
			)}
			{popperText ? (
				<Manager>
					<Reference>
						{({ ref }) => (
							<Toggle
								id={labelId}
								testId="servicedesk-incident-management-settings.common.ui.feature-toggle-v2.toggle-stateless"
								isChecked={value}
								isDisabled={disabled && !popperText}
								size="large"
								ref={ref}
								onChange={handleChange}
								onBlur={() => setIsPopperOpen(false)}
							/>
						)}
					</Reference>
					{isPopperOpen && (
						<Popper placement="bottom-start">
							{({ ref, style }) => (
								<Box
									testId="servicedesk-incident-management-settings.common.ui.feature-toggle-v2.popper"
									ref={ref}
									// 'style' is required by the Popper component to render properly
									// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									style={style}
									xcss={ff('enable_jsm_repackaging_changes_vtwk9') ? popupStyles : popupStylesOld}
									aria-live="polite"
								>
									{popperText}
								</Box>
							)}
						</Popper>
					)}
				</Manager>
			) : (
				<Toggle
					id={labelId}
					testId="servicedesk-incident-management-settings.common.ui.feature-toggle-v2.toggle-stateless"
					isChecked={value}
					isDisabled={disabled || saving}
					size="large"
					ref={toggleRef}
					onChange={handleChange}
				/>
			)}
		</Wrapper>
	);
};

const popupStylesOld = xcss({
	padding: 'space.200',
	maxWidth: '220px',
	borderRadius: 'border.radius.100',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
});

const popupStyles = xcss({
	padding: 'space.200',
	maxWidth: '320px',
	borderRadius: 'border.radius.100',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	zIndex: 'dialog',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FloatLeftWrapper = styled.div({
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	top: 0,
	bottom: 0,
	left: token('space.negative.250', '-20px'),
});
